/**
 * Voice activity detection — Silero VAD v6 via onnxruntime-web.
 *
 * Expects 16 kHz mono Float32 PCM frames of exactly {@link FRAME_SAMPLES}
 * samples (32 ms = 512 samples) and returns a probability in `[0, 1]` where
 * `>= 0.5` is considered voiced.
 */

export const FRAME_SAMPLES = 512; // 32 ms @ 16 kHz — Silero's expected window
export const SAMPLE_RATE = 16_000;
/** Probability to enter speech (start trigger). Matches Android `Utterer.startThreshold`. */
export const VOICED_THRESHOLD = 0.5;
/** Probability below which a frame counts as silent for end-of-utterance. */
export const UNVOICED_THRESHOLD = 0.35;

export interface Vad {
  /** Called once before the first {@link process} call. Idempotent. */
  init(): Promise<void>;
  /** Reset any per-utterance state (e.g. LSTM hidden state for Silero). */
  reset(): void;
  /**
   * Returns the voiced probability for one 32 ms frame. Frames shorter or
   * longer than {@link FRAME_SAMPLES} are an error — caller must batch.
   */
  process(frame: Float32Array): Promise<number>;
  /** Optional release of WASM / model handles. */
  dispose?(): void;
}

// --- Silero VAD ----------------------------------------------------------
//
// Loads `silero_vad.onnx` from the same origin (served from public/ in dev
// and the Vite bundle in prod). Maintains the model's recurrent hidden
// state (`h`, `c`) across frames within an utterance, resetting on
// {@link reset}.

import * as ort from "onnxruntime-web";

export class SileroVad implements Vad {
  private session: ort.InferenceSession | null = null;
  private state: ort.Tensor | null = null;
  private context: Float32Array = new Float32Array(64); // previous chunk tail
  private readonly modelUrl: string;

  constructor(modelUrl = "/app/silero_vad.onnx") {
    this.modelUrl = modelUrl;
  }

  async init(): Promise<void> {
    if (this.session) return;
    // Tell onnxruntime-web where its wasm artifacts live. In production the
    // files are deployed under /app/ort/; in dev Vite serves them from
    // node_modules so we leave the default path.
    if (import.meta.env.PROD) {
      ort.env.wasm.wasmPaths = "/app/ort/";
    }
    this.session = await ort.InferenceSession.create(this.modelUrl, {
      executionProviders: ["wasm"],
      graphOptimizationLevel: "all",
    });
    this.reset();
  }

  reset(): void {
    // Silero v5+/v6 uses a single state tensor of shape [2, 1, 128]
    // and requires a 64-sample context (tail of previous chunk) prepended.
    this.state = new ort.Tensor("float32", new Float32Array(2 * 1 * 128), [2, 1, 128]);
    this.context = new Float32Array(64);
  }

  async process(frame: Float32Array): Promise<number> {
    if (!this.session || !this.state) {
      throw new Error("SileroVad.init() not called");
    }
    if (frame.length !== FRAME_SAMPLES) {
      throw new Error(`SileroVad: expected ${FRAME_SAMPLES}-sample frames, got ${frame.length}`);
    }
    // Silero v5+/v6 requires [1, context+chunk] = [1, 576] input.
    const x = new Float32Array(64 + FRAME_SAMPLES);
    x.set(this.context, 0);
    x.set(frame, 64);
    const input = new ort.Tensor("float32", x, [1, 64 + FRAME_SAMPLES]);
    const sr = new ort.Tensor("int64", BigInt64Array.from([BigInt(SAMPLE_RATE)]), []);
    const out = await this.session.run({ input, sr, state: this.state });
    this.state = out.stateN as ort.Tensor;
    // Save tail of this chunk as context for next call.
    this.context = frame.slice(-64);
    const data = out.output!.data as Float32Array;
    return data[0] ?? 0;
  }

  dispose(): void {
    this.session?.release().catch(() => {});
    this.session = null;
    this.state = null;
  }
}
